/**
 * TAR parser for Gradient Health dicomweb ZIP files.
 *
 * Gradient Health wraps DICOM instances in per-series .tar archives:
 *   dicomweb/studies/<studyUID>/series/<seriesUID>.tar
 *     -> instances/<instanceUID>.dcm
 *
 * This utility extracts those .dcm files and injects them back into
 * the JSZip instance so downstream code sees the flat structure it expects.
 */

import JSZip from 'jszip'

const TAR_BLOCK_SIZE = 512

interface TarFile {
  name: string
  data: ArrayBuffer
}

// Parse a TAR buffer into an array of { name, data } entries.
export function parseTar(tarBuffer: ArrayBuffer): TarFile[] {
  const files: TarFile[] = []
  const view = new Uint8Array(tarBuffer)
  let offset = 0

  while (offset + TAR_BLOCK_SIZE <= view.length) {
    const header = view.slice(offset, offset + TAR_BLOCK_SIZE)

    // End-of-archive marker (all zeros)
    if (header.every(b => b === 0)) break

    // Filename: bytes 0-99, null-terminated
    const nameBytes = header.slice(0, 100)
    const nameEnd = nameBytes.indexOf(0)
    const name = new TextDecoder().decode(
      nameBytes.slice(0, nameEnd > 0 ? nameEnd : 100),
    )

    // File size: bytes 124-135, octal
    const sizeBytes = header.slice(124, 136)
    const sizeStr = new TextDecoder()
      .decode(sizeBytes)
      .replace(/\0/g, '')
      .trim()
    const fileSize = parseInt(sizeStr, 8) || 0

    // Type flag: byte 156, '0'/'\0' = regular file
    const typeFlag = header[156]

    offset += TAR_BLOCK_SIZE

    if (fileSize > 0 && (typeFlag === 0 || typeFlag === 48)) {
      const fileData = tarBuffer.slice(offset, offset + fileSize)
      files.push({ name, data: fileData })
    }

    // Advance past data (padded to 512-byte boundary)
    offset += Math.ceil(fileSize / TAR_BLOCK_SIZE) * TAR_BLOCK_SIZE
  }

  return files
}

// Extract .tar entries from a JSZip instance matching the Gradient Health
// dicomweb path structure, replacing them with the .dcm files inside.
export async function extractTarEntries(zipContent: JSZip): Promise<number> {
  const tarEntries = Object.keys(zipContent.files).filter(
    entry =>
      entry.toLowerCase().endsWith('.tar') &&
      entry.includes('dicomweb') &&
      entry.includes('/studies/') &&
      entry.includes('/series/'),
  )

  if (tarEntries.length === 0) return 0

  let totalExtracted = 0

  for (const tarEntry of tarEntries) {
    const tarFile = zipContent.files[tarEntry]
    if (tarFile.dir) continue

    const tarBuffer = await tarFile.async('arraybuffer')
    const extractedFiles = parseTar(tarBuffer)

    // Replace "series/<seriesUID>.tar" with "series/<seriesUID>/<contents>"
    const basePath = tarEntry.replace(/\.tar$/i, '/')

    for (const { name, data } of extractedFiles) {
      zipContent.file(basePath + name, data)
      totalExtracted++
    }

    zipContent.remove(tarEntry)
  }

  return totalExtracted
}
